import { REIInsightsData } from './../../interfaces/reiinsightsData';
import { LBMInsightsPlotData } from './../../interfaces/insightsPlotData';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, NEVER, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { FiltersValuesLBM, HeatMapFilterResponse } from 'src/app/interfaces';
import { HoverData } from 'src/app/types';
import { environment } from 'src/environments/environment';
import { ViewContext } from '../../enums';
import { saveAs } from 'file-saver';
import {
	HttpResponse,
	LBMInsightsData,
	MatchMakerResponse,
	SearchData,
	UserHistory,
	UserPreference,
} from './../../interfaces';
import { PostFilters } from 'src/app/interfaces/post-filters';
import { AccessRequest } from 'src/app/interfaces/accessRequest';
import { FiltersValuesREI } from 'src/app/interfaces/filters-values-rei';
import { PostFiltersREI } from 'src/app/interfaces/post-filters-rei';
import { AssociatePlot } from 'src/app/interfaces/associate-plot';
import {
	DigitalCoreWrapper,
	KpiRangeDate,
	Kpis,
	KpisCategories,
	KpisChart,
	KpisLocation,
	KpisVisualizationType,
} from 'src/app/types/kpisData';
import { ContilioProject } from 'src/app/interfaces/contilioProjects';

@Injectable()
export class HttpService {
	private hoverDataCache: { [key: string]: Observable<HoverData> } = {};
	constructor(public http: HttpClient) {}

	public getHoverData(params: {
		layerType: Lowercase<ViewContext>;
		id: string;
		cityId: string;
	}): Observable<HoverData> {
		if (!params.id || !params.layerType) {
			return NEVER;
		}
		if (typeof this.hoverDataCache[params.id.concat(params.layerType)] === 'undefined') {
			this.hoverDataCache[params.id.concat(params.layerType)] = this.http
				.get<HoverData>(
					`${environment.baseUrl}/data/hover?type=${params.layerType}&id=${
						params.id
					}&scope=${params.cityId || '2'}`,
				)
				.pipe(shareReplay(1));
		}
		return this.hoverDataCache[params.id.concat(params.layerType)];
	}

	public getFilteredSummary(params: {
		assetId: string;
		type: ViewContext;
		body: any;
		mode: string;
	}): Observable<LBMInsightsData> {
		return this.http.post<LBMInsightsData>(
			`${environment.baseUrl}/data/summary-filter?type=${params.type}&id=${params.assetId}&version=${environment.version}&mode=${params.mode}`,
			params.body,
		);
	}

	public geJumpToData(searchTerm: string, app: string): Observable<SearchData> {
		return this.http.get<SearchData>(
			`${environment.baseUrl}/data/search?term=${searchTerm}&limit=500&version=${environment.version}&type=${app}`,
		);
	}

	public searchWithFilters(
		searchTerm: string,
		app: string,
		filters: Array<string>,
		pattern: number,
	): Observable<SearchData> {
		let url = `${environment.baseUrl}/data/search?term=${searchTerm}&scope=${filters}&version=${environment.version}&type=${app}`;
		if (pattern !== 0) {
			url += `&asteriskPos=${pattern}`;
		}
		url = btoa(url);
		return this.http.get<SearchData>(atob(url));
	}

	public getLBMInsightsData(params: {
		assetId: string;
		type: ViewContext;
	}): Observable<LBMInsightsData | LBMInsightsPlotData> {
		return this.http.get<LBMInsightsData | LBMInsightsPlotData>(
			`${environment.baseUrl}/data/summary?type=${params.type}&id=${params.assetId}&version=${environment.version}`,
		);
	}
	public getREIInsightsData(params: {
		assetId: string;
		floorId: string;
		type: ViewContext;
	}): Observable<REIInsightsData> {
		let url = `${environment.baseUrl}/data/rei-summary?type=${params.type}&id=${params.assetId}&version=${environment.version}`;
		if (params.type === ViewContext.Floor) {
			url += '&floorId=' + params.floorId;
		}
		return this.http.get<REIInsightsData>(url);
	}

	public getUserPreferences(email: string): Observable<UserPreference> {
		return this.http
			.get<HttpResponse>(`${environment.baseUrl}/profile/user/user-preference/v2/${email}`, {
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			})
			.pipe(map((data: HttpResponse) => data.response as UserPreference));
	}

	public getRecentHistory(): Observable<UserHistory[]> {
		return this.http
			.get<HttpResponse>(`${environment.baseUrl}/profile/user/history`, {
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			})
			.pipe(map((data: HttpResponse) => data.response as UserHistory[]));
	}

	//Invalidate JWT token on clicking sign out
	public invalidateJWT(): Observable<any> {
		return this.http.get<HttpResponse>(`${environment.baseUrl}/profile/user/invalidate`, {
			headers: {
				'Content-Type': 'application/json',
				'Access-Control-Allow-Origin': environment.accessControl,
			},
		});
	}

	public postRecentHistory(userHistory: UserHistory): Observable<UserHistory> {
		return this.http
			.post<HttpResponse>(`${environment.baseUrl}/profile/user/history`, userHistory, {
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			})
			.pipe(map((data: HttpResponse) => data.response as UserHistory));
	}

	public postStreamer(data: any) {
		return this.http.post<HttpResponse>(
			`${environment.baseUrl}/profile/user/connection/`,
			data,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}

	public updateUserPreferences(userPreference: UserPreference): Observable<UserPreference> {
		const userId = userPreference.userId;
		let userPreferenceObject = structuredClone(userPreference);

		if (!userPreferenceObject.user_preference_id) {
			userPreferenceObject.user_preference_id = userPreferenceObject.userId;
		}
		userPreferenceObject.app_version = '2';
		userPreferenceObject.is_showinsights = false;
		userPreferenceObject.is_zoom_slider = false;
		return this.http
			.post<HttpResponse>(
				`${environment.baseUrl}/profile/user/user-preference/${userId}`,
				userPreferenceObject,
				{
					headers: {
						'Content-Type': 'application/json',
						'Access-Control-Allow-Origin': environment.accessControl,
					},
				},
			)
			.pipe(map((data: HttpResponse) => data.response as UserPreference));
	}

	getFiltersValues(label: string, id: string) {
		return this.http.get<FiltersValuesLBM>(
			`${environment.baseUrl}/data/gis-filter-index?id=${id}&label=${label}&version=${environment.version}`,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}
	getFiltersValuesREI(label: string, id: string) {
		return this.http.get<FiltersValuesREI>(
			`${environment.baseUrl}/data/gis-filter-rei-index?id=${id}&label=${label}&version=${environment.version}`,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}

	getTableauJwt() {
		return this.http.get<any>(
			`${environment.baseUrl}/profile/tableau-auth`,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}

	//Get the Available/Free streamers
	public getVMStatus(): Observable<MatchMakerResponse[]> {
		return this.http.get<MatchMakerResponse[]>(
			`${environment.matchMakerUrl}/match-maker/v1/free-cs`,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}

	public getHeatmapFilters(lvl: ViewContext): Observable<HeatMapFilterResponse> {
		return this.http.get<HeatMapFilterResponse>(
			`${environment.baseUrl}/data/heat-map-filters?type=${lvl}&version=${environment.version}`,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}

	public heatmapDevelopment(
		label: string,
		id: string,
		value: string,
		request: string,
	): Observable<any> {
		let url = `${environment.baseUrl}/data/heat-map?id=${id}&label=${label}&development=${value}&version=${environment.version}&request=${request}`;
		url = btoa(url);
		return this.http.get<any>(atob(url), {
			headers: {
				'Content-Type': 'application/json',
				'Access-Control-Allow-Origin': environment.accessControl,
			},
		});
	}
	public heatmapSale(label: string, id: string, value: string, request: string): Observable<any> {
		let url = `${environment.baseUrl}/data/heat-map?id=${id}&label=${label}&sales=${value}&version=${environment.version}&request=${request}`;
		url = btoa(url);
		return this.http.get<any>(atob(url), {
			headers: {
				'Content-Type': 'application/json',
				'Access-Control-Allow-Origin': environment.accessControl,
			},
		});
	}

	public heatmapPlot(label: string, id: string, value: string, request: string): Observable<any> {
		return this.http.get<any>(
			`${environment.baseUrl}/data/heat-map?id=${id}&label=${label}&version=${environment.version}&request=${request}&heatMapTypeId=${value}`,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}

	public postFilters(filter: PostFilters, request: string) {
		let filters: any = { ...filter };
		if (filter.is_sold !== '1') delete filters.year_of_sale;
		return this.http.post(
			`${environment.baseUrl}/data/gis-filter?version=${environment.version}&request=${request}`,
			filters,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}
	public postREIFiltersBuildings(filter: PostFiltersREI, request: string) {
		let filters: any = { ...filter };
		return this.http.post(
			`${environment.baseUrl}/data/gis-rei-filter?version=${environment.version}&request=${request}`,
			filters,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}
	public postREIFiltersSupply(filter: PostFiltersREI, request: string) {
		let filters: any = { ...filter };
		return this.http.post(
			`${environment.baseUrl}/data/gis-rei-supply-demand-filter?version=${environment.version}&request=${request}`,
			filters,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}
	public postREIFiltersRent(filter: PostFiltersREI, request: string) {
		let filters: any = { ...filter };
		return this.http.post(
			`${environment.baseUrl}/data/gis-rei-rent-filter?version=${environment.version}&request=${request}`,
			filters,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}
	public postREIFiltersSales(filter: PostFiltersREI, request: string) {
		let filters: any = { ...filter };
		return this.http.post(
			`${environment.baseUrl}/data/gis-rei-sales-filter?version=${environment.version}&request=${request}`,
			filters,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}
	public postREIFiltersHospitality(filter: PostFiltersREI, request: string) {
		let filters: any = { ...filter };
		return this.http.post(
			`${environment.baseUrl}/data/gis-rei-hospitality-filter?version=${environment.version}&request=${request}`,
			filters,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}


	public saveAsNow(blob: Blob, fileName: string) {
		saveAs(blob, fileName);
	}

	public downloadPdfFromUrl(url: string, fileName: string, token: string): any {
		try {
			const httpOptions: Object = {
				headers: new HttpHeaders({
					Authorization: `Bearer ${token}`,
				}),
				responseType: 'blob',
			};
			return this.http.get(url, httpOptions).subscribe((res) => {
				const response = res as Blob;
				const blob = new Blob([response], {
					type: 'application/pdf',
				});
				//saveAs(blob, fileName);
				this.saveAsNow(blob, fileName);
			});
		} catch (e: any) {
			throw new Error(e);
		}
	}

	public createAccessRequest(userData: AccessRequest): Observable<any> {
		return this.http.post<HttpResponse>(
			`${environment.baseUrl}/data/access-request`,
			userData,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}

	public getLandingPageContent(): Observable<any> {
		return this.http.get<any>(`${environment.baseUrl}/cms/landing-page?populate=*`, {
			headers: {
				'Content-Type': 'application/json',
				'Access-Control-Allow-Origin': environment.accessControl,
			},
		});
	}

	public checkInsightHubAccess(email: any): Observable<any> {
		return this.http.get<any>(`${environment.baseUrl}/data/aldar-insights?isAccess=${email}`, {
			headers: {
				'Content-Type': 'application/json',
				'Access-Control-Allow-Origin': environment.accessControl,
			},
		});
	}

	public getFaqList(): Observable<any> {
		return this.http.get<any>(`${environment.baseUrl}/cms/faqs`, {
			headers: {
				'Content-Type': 'application/json',
				'Access-Control-Allow-Origin': environment.accessControl,
			},
		});
	}

	public getPostLoginPageContent(): Observable<any> {
		return this.http.get<any>(`${environment.baseUrl}/cms/post-login-page?populate=*`, {
			headers: {
				'Content-Type': 'application/json',
				'Access-Control-Allow-Origin': environment.accessControl,
			},
		});
	}

	public getDashboardStaticContent(): Observable<any> {
		return this.http.get<any>(`${environment.baseUrl}/cms/dashboard?populate=*`, {
			headers: {
				'Content-Type': 'application/json',
				'Access-Control-Allow-Origin': environment.accessControl,
			},
		});
	}

	public getInsightsStaticContent(): Observable<any> {
		return this.http.get<any>(`${environment.baseUrl}/cms/insight?populate=*`, {
			headers: {
				'Content-Type': 'application/json',
				'Access-Control-Allow-Origin': environment.accessControl,
			},
		});
	}

	public getToastrMessages(): Observable<any> {
		return this.http.get<any>(`${environment.baseUrl}/cms/toastr-message`, {
			headers: {
				'Content-Type': 'application/json',
				'Access-Control-Allow-Origin': environment.accessControl,
			},
		});
	}

	public getTooltips(): Observable<any> {
		return this.http.get<any>(`${environment.baseUrl}/cms/tooltip`, {
			headers: {
				'Content-Type': 'application/json',
				'Access-Control-Allow-Origin': environment.accessControl,
			},
		});
	}
	public getGrossaryList(): Observable<any> {
		return this.http.get<any>(`${environment.baseUrl}/cms/glossaries`, {
			headers: {
				'Content-Type': 'application/json',
				'Access-Control-Allow-Origin': environment.accessControl,
			},
		});
	}

	public reportProblem(userData: AccessRequest): Observable<any> {
		return this.http.post<HttpResponse>(`${environment.baseUrl}/data/user-feedback`, userData, {
			headers: {
				'Content-Type': 'application/json',
				'Access-Control-Allow-Origin': environment.accessControl,
			},
		});
	}

	public submitGateway(userData: AccessRequest): Observable<any> {
		return this.http.post<HttpResponse>(
			`${environment.baseUrl}/data/ad-gateways/gateway-request`,
			userData,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}

	public gatewayReview(userData: AccessRequest): Observable<any> {
		return this.http.post<HttpResponse>(
			`${environment.baseUrl}/data/ad-gateways/gateway`,
			userData,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}

	public getGatewayPlotDetails(plotData: AccessRequest): Observable<any> {
		return this.http.post<HttpResponse>(
			`${environment.baseUrl}/data/plot-details`,
			{ plotIds: plotData },
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}

	//API to get the Gateways List
	public getGatewayList(data: any): Observable<any> {
		return this.http.post<HttpResponse>(
			`${environment.baseUrl}/data/ad-gateways/gateway-list/`,
			{ ...data },
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}

	//API to get the Gateways Request List
	public getGatewayRequestList(data: any): Observable<any> {
		return this.http.post<HttpResponse>(
			`${environment.baseUrl}/data/ad-gateways/gateway-request-list/`,
			{ ...data },
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}

	//Get the version of ES API service
	public getVersionDetails(): Observable<any> {
		return this.http.get<any>(`${environment.baseUrl}/data-direct/health`, {
			headers: {
				'Content-Type': 'application/json',
				'Access-Control-Allow-Origin': environment.accessControl,
			},
		});
	}

	public getGlossaryStaticContents(): Observable<any> {
		return this.http.get<any>(`${environment.baseUrl}/cms/help?populate=*`, {
			headers: {
				'Content-Type': 'application/json',
				'Access-Control-Allow-Origin': environment.accessControl,
			},
		});
	}
	public getREIFIlterStaticContents(): Observable<any> {
		return this.http.get<any>(`${environment.baseUrl}/cms/rei-filter`, {
			headers: {
				'Content-Type': 'application/json',
				'Access-Control-Allow-Origin': environment.accessControl,
			},
		});
	}
	public getREIHeatmapStaticContents(): Observable<any> {
		return this.http.get<any>(`${environment.baseUrl}/cms/rei-heatmap`, {
			headers: {
				'Content-Type': 'application/json',
				'Access-Control-Allow-Origin': environment.accessControl,
			},
		});
	}

	public reiHeatMapSupplyAndDemand(
		label: string,
		id: string,
		category: string,
		subCategory: string,
		hospitality: string,
		request: string,
	): Observable<any> {
		let url = '';
		if (subCategory) {
			url = `subCategory=${subCategory}&version=${environment.version}`;
		}
		if (hospitality) {
			url = `hospitalitySubType=${hospitality}&subCategory=${subCategory}&version=${environment.version}`;
		}
		return this.http.post<any>(
			`${environment.baseUrl}/data/rei-heat-map?id=${id}&label=${label}&supplyAndDemand=${category}&${url}&request=${request}`,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}

	public getREIHeatmapFilter(id: string): Observable<any> {
		return this.http.get<any>(
			`${environment.baseUrl}/data/heat-map-rei-filters?version=${environment.version}`,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}

	public reiHeatMap(
		label: string,
		id: string,
		value: string,
		request: string,
		type: string,
		dropdownValues: {},
	): Observable<any> {
		let url = `${environment.baseUrl}/data/rei-heat-map?id=${id}&label=${label}&${type}=${value}&version=${environment.version}&request=${request}`;
		url = btoa(url);
		return this.http.post<any>(atob(url), dropdownValues, {
			headers: {
				'Content-Type': 'application/json',
				'Access-Control-Allow-Origin': environment.accessControl,
			},
		});
	}

	public checkAssociatePlot(label: string, id: string, scope: string): Observable<AssociatePlot> {
		return this.http.get<AssociatePlot>(
			`${environment.baseUrl}/data/check-associate-plot?type=${label}&id=${id}&scope=${scope}`,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}

	public getGatewayProjectDetails(id: string): Observable<AssociatePlot> {
		return this.http.get<AssociatePlot>(
			`${environment.baseUrl}/data/ad-gateways/gateway-details?gatewayId=${id}`,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}

	public getGatewayProjectReviewDetails(id: string): Observable<AssociatePlot> {
		return this.http.get<AssociatePlot>(
			`${environment.baseUrl}/data/ad-gateways/gateway-review-details?guid=${id}`,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}

	public getSuggestions(label: string, field: string): any {
		return this.http.get<AssociatePlot>(
			`${environment.baseUrl}/data/ad-gateways?fieldName=${field}&value=${label}`,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}

	// HTTP call for listing the contilio projrcts
	public getContilioProjectList():Observable<{ response: ContilioProject[]}>{
		return this.http.get<{ response: ContilioProject[]}>(
			`${environment.baseUrl}/data/contillio/projects`,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}
	public getBuildingsAndFloorsDetails(sector_id: string, token: string): Observable<any> {
        const body = {
        community: [sector_id]  
        }
        return this.http.post<HttpResponse>(
            `${environment.baseUrl}/data/v1/kpi/premises`,
            body,
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Access-Control-Allow-Origin': environment.accessControl,
                },
            },
        );
    }


	 /** Frontend-only: Graph -> Blob -> data URL (data:image/*;base64,...) */
public  getUserProfileImageDataUrl(email: string, accessToken: string): Observable<string> {
    const url = `https://graph.microsoft.com/v1.0/users/${email}/photo/$value`;
    const bearer = accessToken.startsWith('Bearer ') ? accessToken : `Bearer ${accessToken}`;
    const headers = new HttpHeaders({ Authorization: bearer });

    return this.http.get(url, { responseType: 'blob', headers }).pipe(
      switchMap((blob) =>
        from(
          new Promise<string>((resolve, reject) => {
            if (!blob || blob.size === 0) return reject('empty-photo');
            const r = new FileReader();
            r.onloadend = () => resolve(String(r.result)); // "data:image/jpeg;base64,..."
            r.onerror = () => reject('read-failed');
            r.readAsDataURL(blob);
          })
        )
      )
    );
  }

  /** Example designation API — adapt the URL/shape to your backend */
public  getUserDesignation(email: string, accessToken: string) {
	 const url = `https://graph.microsoft.com/v1.0/users/${email}`;
    const bearer = accessToken.startsWith('Bearer ') ? accessToken : `Bearer ${accessToken}`;
    return this.http.get<{ jobTitle: string | null }>(
      url,
      { headers: new HttpHeaders({ Authorization: bearer }) }
    );
  }


  //API to get the Gateways Request List
	public getG0ProjectDetails(data: any, myProject: boolean): Observable<any> {
		return this.http.post<HttpResponse>(
			`${environment.baseUrl}/data/gates/project-list?isMyProjectEnabled=${myProject}`,
			{ ...data },
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}
	//API to get the Gateways Request List
	public getG0CardsDetails(): Observable<any> {
		return this.http.get<HttpResponse>(
			`${environment.baseUrl}/data/gates/dashboard/analytics`,
			{
				headers: {
					'Content-Type': 'application/json',
					//'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}
	//API to get the Gateways Request List
	public getG0FilterDetails(): Observable<any> {
		return this.http.get<HttpResponse>(
			`${environment.baseUrl}/data/ad-gateways/gateway-filter`,
			{
				headers: {
					'Content-Type': 'application/json',
				},
			},
		);
	}

	//API to get the Contilio project Lists
	public getConstructionManagementProjectLists(): Observable<any> {
		return this.http.get<HttpResponse>(
			`${environment.baseUrl}/data/v1/contilio/projects`,
			{
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': environment.accessControl,
				},
			},
		);
	}

	public getKpisTypeViews(idToken: string): Observable<DigitalCoreWrapper<KpisVisualizationType[]>> {
		const bearer = idToken.startsWith('Bearer ') ? idToken : `Bearer ${idToken}`;
		const headers = new HttpHeaders({ Authorization: bearer });

		return this.http.get<DigitalCoreWrapper<KpisVisualizationType[]>>(`${environment.digitalCoreUrl}/Visualization/GetVisualizationTypes`, {
			headers
		});
	}

	public getKpisCategories(typeView: string, idToken: string): Observable<DigitalCoreWrapper<KpisCategories[]>> {
		const bearer = idToken.startsWith('Bearer ') ? idToken : `Bearer ${idToken}`;
		const headers = new HttpHeaders({ Authorization: bearer });

		return this.http.get<DigitalCoreWrapper<KpisCategories[]>>(`${environment.digitalCoreUrl}/Category/GetCategories`, {
			headers
		});
	};

	public getKpis(locationId: string, typeView: string, idToken: string): Observable<DigitalCoreWrapper<Kpis[]>> {
		const bearer = idToken.startsWith('Bearer ') ? idToken : `Bearer ${idToken}`;
		const headers = new HttpHeaders({ Authorization: bearer });

		return this.http.get<DigitalCoreWrapper<Kpis[]>>(`${environment.digitalCoreUrl}/Kpi/GetKpis/${locationId}`, {
			headers
		});
	};

	public getKpiCharts(locationId: string, typeView: string, categoryId: number, rangeDate: KpiRangeDate, idToken: string): Observable<KpisChart> {
		const bearer = idToken.startsWith('Bearer ') ? idToken : `Bearer ${idToken}`;
		const headers = new HttpHeaders({ Authorization: bearer });
		
		return this.http.post<KpisChart>(
			`${environment.digitalCoreUrl}/Kpi/GetCharts`,
			{ aldarId: locationId, categoryId: categoryId, timeSpan: rangeDate },
			{ headers },
		);
	}

	public getLocations(locationId: string, idToken: string): Observable<DigitalCoreWrapper<KpisLocation>> {
		const bearer = idToken.startsWith('Bearer ') ? idToken : `Bearer ${idToken}`;
		const headers = new HttpHeaders({ Authorization: bearer });

		return this.http.get<DigitalCoreWrapper<KpisLocation>>(`${environment.digitalCoreUrl}/Location/GetLocation/${locationId}`, {
			headers
		});
	}
}
